import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, X } from 'lucide-react';

export interface SettingsCrumb {
  label: string;
  to?: string;
}

export const SettingsPath: React.FC<{ items: SettingsCrumb[] }> = ({ items }) => (
  <nav className="set-path" aria-label={items.map((item) => item.label).join(' / ')}>
    <ol>
      {items.map((item, index) => {
        const current = index === items.length - 1;
        return (
          <li key={`${item.label}-${index}`}>
            {index > 0 ? <ChevronRight className="set-path__sep" size={14} aria-hidden="true" /> : null}
            {item.to && !current ? (
              <Link to={item.to}>{item.label}</Link>
            ) : (
              <span aria-current={current ? 'page' : undefined}>{item.label}</span>
            )}
          </li>
        );
      })}
    </ol>
  </nav>
);

export const SettingsDialog: React.FC<{
  title: string;
  subtitle: string;
  closeLabel: string;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ title, subtitle, closeLabel, onClose, children }) => {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div className="set-dialog" role="presentation">
      <button type="button" className="set-dialog__backdrop" aria-label={closeLabel} onClick={onClose} />
      <div className="set-dialog__panel" role="dialog" aria-modal="true" aria-labelledby="set-dialog-title">
        <div className="set-dialog__head">
          <div>
            <h2 id="set-dialog-title">{title}</h2>
            <p>{subtitle}</p>
          </div>
          <button type="button" className="set-dialog__close" aria-label={closeLabel} onClick={onClose}>
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};
